import RootStore from '@App/stores/root-store'
import { TransferPageState } from '@App/stores/rightsholder/catalogTransfer/catalog-transfer-list-store'
import { CatalogTransferSearchQuery } from '@App/stores/search/models/catalog-transfer-search'
import { action, makeObservable, observable } from 'mobx'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { defaultBebopPagination } from '@App/stores/rightsholder/registrationHistory/registration-history-tab-store'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { mapCriteriaToObject } from '@App/helpers/map-criteria-to-object'
import { findPageOnLimitChange } from '@App/helpers/pagination/find-page-on-limit-change'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { AxiosResponse } from 'axios'
import { ResultPage } from '@Entities/result-page'
import { CatalogTransferView } from '@Entities/rightsholder/catalog-transfer-view'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import { generateSearchCriterionFromOption } from '@App/helpers/generate-search-criterion'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { v4 as uuidv4 } from 'uuid'
import { CatalogTransferFilter } from '@App/forms/filters/catalog-transfer-filter'
import { isEmpty } from 'lodash'

export type CatalogTransferSearchFunction = (
    rightsholderId: number,
    query: CatalogTransferSearchQuery,
    pagination: PaginationBody
) => Promise<AxiosResponse<ResultPage<CatalogTransferView>>>

export const SEARCH_CATALOG_TRANSFER_OPTIONS: Array<SelectOptionHide<CatalogTransferSearchQuery>> = [
    {
        id: uuidv4(),
        value: 'transferId',
        label: 'Catalog Transfer ID',
        isPrimary: true,
    },
    {
        id: uuidv4(),
        value: 'sourcePublisherName',
        label: 'Source Publisher Name',
        isPrimary: true,
    },
]
export const defaultCatTTranSelectionCriteria: Array<SearchCriterion<CatalogTransferSearchQuery>> = [
    generateSearchCriterionFromOption(SEARCH_CATALOG_TRANSFER_OPTIONS[0], true),
]
/**
 * Which list a tab store serves. It is part of the query key and a literal union, not a free string, because two
 * stores that reach different endpoints and shared a scope would be served each other's cached pages.
 */
export type CatalogTransferTabScope = 'LIST' | 'REVIEW'

export const DEFAULT_FILTERS: CatalogTransferFilter = { sort_by: 'created_at' }
const DEFAULT_SORT: PaginationBody['sort'] = { field: 'updated_at', direction: 'DESC' }
export default class CatalogTransferTabStore {
    private rootStore: RootStore
    private _currentQuery: CatalogTransferSearchQuery
    private _currentFilter: CatalogTransferFilter
    private _catalogTransferData: TransferPageState = {}
    private _pagination: PaginationBody
    private _paginationInfo: PaginationInfo | undefined
    private _dataVersion = 0
    private _criteriaStore: SearchCriteriaStore<CatalogTransferSearchQuery>
    private readonly catalogTransferGetFunction: CatalogTransferSearchFunction

    /** `scope` keeps this tab's cached searches apart from the other tab's (it is part of the query key). */
    constructor(
        rootStore: RootStore,
        catalogTransferGetFunction: CatalogTransferSearchFunction,
        private readonly scope: CatalogTransferTabScope
    ) {
        makeObservable<
            CatalogTransferTabStore,
            | '_currentQuery'
            | '_currentFilter'
            | '_catalogTransferData'
            | '_pagination'
            | '_paginationInfo'
            | '_dataVersion'
            | '_criteriaStore'
        >(this, {
            _currentQuery: observable,
            _currentFilter: observable,
            _catalogTransferData: observable,
            _pagination: observable,
            _paginationInfo: observable,
            _dataVersion: observable,
            _criteriaStore: observable,
            setCurrentQuery: action,
            setCurrentFilter: action,
            setCurrentPage: action,
            setLimit: action,
            seedSearchFromUrl: action,
            startSearch: action,
            applyFilters: action,
            reconcileFilter: action,
            invalidate: action,
            applyResults: action,
            clearResults: action,
            reset: action,
        })
        this.rootStore = rootStore
        this._currentQuery = {}
        this._currentFilter = DEFAULT_FILTERS
        this._pagination = defaultBebopPagination
        this._criteriaStore = this.createCatalogTransferListCriteriaStore()
        this.catalogTransferGetFunction = catalogTransferGetFunction
    }

    get currentQuery() {
        return this._currentQuery
    }

    get currentFilter() {
        return this._currentFilter
    }

    get catalogTransferData() {
        return this._catalogTransferData.page
    }

    get pagination() {
        return this._pagination
    }

    get paginationInfo() {
        return this._paginationInfo
    }

    get limit(): number {
        return this._pagination.limit ?? 10
    }

    get criteriaStore() {
        return this._criteriaStore
    }

    /**
     * Part of the query key, bumped whenever the data has to be fetched again even though nothing the server
     * sees has changed: a submit, a reset, or another part of the app changing the transfers (see `invalidate`).
     */
    get dataVersion(): number {
        return this._dataVersion
    }

    /** Identifies the data set this store searches, so tabs and rightsholders never share cached results. */
    get searchScopeKey(): unknown[] {
        return ['catalog-transfers', this.scope, this.rootStore.rightsholderStore.currentRightsholderId]
    }

    /** The transfers changed elsewhere (e.g. one was deleted or submitted): fetch again, from the first page. */
    invalidate() {
        this._pagination.page = 1
        this._dataVersion += 1
    }

    /** Makes the submitted terms and filter the search, from page 1, and discards the previous results. */
    startSearch(query: CatalogTransferSearchQuery, filter?: CatalogTransferFilter) {
        this._currentQuery = query
        this.setCurrentFilter(filter)
        this.startFreshSearch()
    }

    /** Applies the filters on their own (the sidebar's Apply Filters), keeping the search terms. */
    applyFilters(filter?: CatalogTransferFilter) {
        this.setCurrentFilter(filter)
        this.startFreshSearch()
    }

    /**
     * Brings the filter in line with what a fresh visit should show (see `useCatalogTransferFilters`). The store
     * outlives visits, so this also goes back to the first page: page 3 of the old filter's results says nothing
     * about the new filter's, which may have no page 3.
     */
    reconcileFilter(filter?: CatalogTransferFilter) {
        this.setCurrentFilter(filter)
        this._pagination = defaultBebopPagination
        this.clearResults()
    }

    clearResults() {
        this._catalogTransferData = {}
        this._paginationInfo = undefined
    }

    reportSearchError(error: unknown) {
        this.rootStore.popupStore.setAlertFromError(error)
    }

    setCurrentQuery(currentQuery: CatalogTransferSearchQuery) {
        this._currentQuery = currentQuery
    }

    setCurrentFilter(currentFilter?: CatalogTransferFilter) {
        if (!currentFilter || isEmpty(currentFilter)) {
            this._currentFilter = DEFAULT_FILTERS
        } else {
            this._currentFilter = currentFilter
        }
    }

    setCurrentPage(page: number) {
        this._pagination.page = page
    }

    setLimit(num: number): void {
        if (this._catalogTransferData) {
            const newPage = findPageOnLimitChange(
                this._pagination,
                this._catalogTransferData.page?.totalElements,
                num,
                1
            )
            this.setCurrentPage(newPage)
        }
        this._pagination.limit = num
    }

    /**
     * Performs the search request without touching any observable state, so it can be owned by an external cache
     * (TanStack Query). The sort comes from the filter, falling back to the most recently updated.
     */
    async fetchCatalogTransfers(
        query: CatalogTransferSearchQuery,
        filter: CatalogTransferFilter,
        pagination: PaginationBody
    ): Promise<ResultPage<CatalogTransferView>> {
        const sortedPagination: PaginationBody = {
            ...pagination,
            sort: filter.sort_by ? { field: filter.sort_by, direction: 'DESC' } : DEFAULT_SORT,
        }
        const result = await this.catalogTransferGetFunction(
            this.rootStore.rightsholderStore.currentRightsholderId,
            { statuses: filter.statusFilter, ...query },
            sortedPagination
        )
        return result.data
    }

    /** Stores results fetched elsewhere so the table, which reads the store, stays in sync. */
    applyResults(page: ResultPage<CatalogTransferView>, pagination: PaginationBody) {
        this._catalogTransferData.page = page
        this.setPaginationInfo(page.totalElements, pagination)
    }

    /**
     * Starts a visit from a link's search terms. Run it before the tab first renders: it swaps in a criteria
     * store seeded from the URL, makes the terms the search if they satisfy the form's own "ready to search"
     * rule, and marks the results stale so the mount's refresh runs that search.
     */
    seedSearchFromUrl(criteria: Record<string, unknown>) {
        this._criteriaStore = this.createCatalogTransferListCriteriaStore()
        this._criteriaStore.applyCriteriaFromObject(criteria)
        const rows = this._criteriaStore.searchCriteria
        this._currentQuery = this._criteriaStore.isCriteriaSearchReady(rows) ? mapCriteriaToObject(rows) : {}
        this._pagination = defaultBebopPagination
        this.clearResults()
    }

    reset() {
        this._currentQuery = {}
        this._currentFilter = DEFAULT_FILTERS
        this._criteriaStore = this.createCatalogTransferListCriteriaStore()
        this.startFreshSearch()
    }

    private startFreshSearch() {
        this._pagination = defaultBebopPagination
        this.clearResults()
        this._dataVersion += 1
    }

    private createCatalogTransferListCriteriaStore(): SearchCriteriaStore<CatalogTransferSearchQuery> {
        const maxCriteria = 1
        return new SearchCriteriaStore(
            defaultCatTTranSelectionCriteria,
            SEARCH_CATALOG_TRANSFER_OPTIONS,
            SearchOptionType.primary,
            RequiredCriteria.none,
            maxCriteria
        )
    }

    private setPaginationInfo(total: number, pagination: PaginationBody) {
        if (!pagination.limit || !pagination.page) {
            return
        }
        this._paginationInfo = calcNumPages(total, pagination.limit, pagination.page, pagination.refined)
    }
}
